import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Upload, RefreshCw, Download } from 'lucide-react';
import { tripGrossInclGST, getDisplayStatus, exportCSV } from '../../utils/reportHelpers';
import { tripHasConvertedFare } from '../../utils/flightBooking';

const TOLERANCE = 0.02;

const normalize = (v) => String(v || '').replace(/\D/g, '');

const fmt$ = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtDiff = (diff) => {
  if (diff == null) return '—';
  const abs  = Math.abs(diff);
  const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
  return `${sign}$${abs.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const ALL_STATUSES = ['draft', 'pending_approval', 'approved', 'declined', 'booked', 'travelling', 'completed', 'cancelled'];

const STATUS_LABEL = {
  draft:            'Draft',
  pending_approval: 'Pending',
  approved:         'Approved',
  declined:         'Declined',
  booked:           'Booked',
  cancelled:        'Cancelled',
  travelling:       'Travelling',
  completed:        'Completed',
};

const STATUS_PILL = {
  draft:            'bg-gray-100 text-gray-600',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved:         'bg-emerald-100 text-emerald-800',
  declined:         'bg-red-100 text-red-700',
  booked:           'bg-blue-100 text-blue-800',
  cancelled:        'bg-purple-100 text-purple-700',
  travelling:       'bg-teal-100 text-teal-800',
  completed:        'bg-gray-200 text-gray-700',
};

export default function VTOReconciliationReport({ trips }) {
  const navigate = useNavigate();

  const [vtoRows,       setVtoRows]       = useState(null);
  const [fileName,      setFileName]      = useState('');
  const [filterMode,    setFilterMode]    = useState('nonmatching');
  const [statusFilter,  setStatusFilter]  = useState([...ALL_STATUSES]);
  const [excludePast,   setExcludePast]   = useState(false);
  const [search,        setSearch]        = useState('');
  const [dragOver,      setDragOver]      = useState(false);
  const fileRef = useRef();

  const toggleStatus = (s) =>
    setStatusFilter(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );

  const parseFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) {
      alert('Please upload an Excel (.xlsx or .xls) file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const parsed = rows
          .slice(1)
          .filter((r) => r[0] != null && r[0] !== '')
          .map((r) => ({
            tripId:        String(r[0]).trim(),
            client:        String(r[2] || '').trim(),
            travelDate:    String(r[3] || '').trim(),
            gross:         parseFloat(r[4]) || 0,
            received:      parseFloat(r[5]) || 0,
            clientBalance: parseFloat(r[6]) || 0,
          }));
        setVtoRows(parsed);
        setFileName(file.name);
        setFilterMode('nonmatching');
        setExcludePast(false);
        setSearch('');
      } catch {
        alert('Could not parse the file. Please ensure it is a valid Balance Summary Audit export.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const { matchedRows, noRefTrips, summary } = useMemo(() => {
    if (!vtoRows) return { matchedRows: [], noRefTrips: [], summary: null };

    const stxTrips = trips.filter((t) => t.tripType === 'STX-Managed');

    const vtoMap = new Map();
    vtoRows.forEach((v) => {
      const key = normalize(v.tripId);
      if (key) vtoMap.set(key, v);
    });

    const noRef = stxTrips
      .filter((t) => !t.vtoTripId)
      .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

    const matched = stxTrips
      .filter((t) => t.vtoTripId)
      .map((t) => {
        const vto        = vtoMap.get(normalize(t.vtoTripId)) || null;
        // Gross, deliberately. VTO holds the full supplier amount and knows nothing about
        // travel credits, so netting one out here would report every credited trip as an
        // amount mismatch against a file that is actually correct.
        const portalGross = tripGrossInclGST(t);
        const vtoGross   = vto ? vto.gross : null;
        const diff       = vto != null ? portalGross - vtoGross : null;
        const isExact    = diff != null && Math.abs(diff) <= TOLERANCE;
        // A fare the airline priced in a foreign currency carries an exchange rate captured at
        // search time, so a small difference against what settled has an innocent explanation.
        // Flagged, never auto-excused: the size of the gap is still a judgement for a person.
        const fxConverted = tripHasConvertedFare(t);
        return { trip: t, vto, portalGross, vtoGross, diff, isExact, fxConverted };
      })
      .sort((a, b) => {
        if (a.vto && !a.isExact && (!b.vto || b.isExact)) return -1;
        if (b.vto && !b.isExact && (!a.vto || a.isExact)) return 1;
        if (!a.vto && b.vto) return -1;
        if (a.vto && !b.vto) return 1;
        return (a.trip.travellerName || '').localeCompare(b.trip.travellerName || '');
      });

    const discrepancies = matched.filter((r) => r.vto && !r.isExact).length;
    const exact         = matched.filter((r) => r.isExact).length;
    const noVtoMatch    = matched.filter((r) => !r.vto).length;

    return {
      matchedRows: matched,
      noRefTrips:  noRef,
      summary: {
        vtoRecords:    vtoRows.length,
        matched:       matched.length,
        discrepancies,
        exact,
        noVtoMatch,
        nonMatching:   discrepancies + noVtoMatch,
        noRef:         noRef.length,
      },
    };
  }, [vtoRows, trips]);

  const today = new Date().toISOString().slice(0, 10);

  const displayNoRef = useMemo(() => {
    if (!noRefTrips) return [];
    let rows = noRefTrips;
    if (excludePast) rows = rows.filter((t) => !t.startDate || t.startDate >= today);
    if (statusFilter.length < ALL_STATUSES.length)
      rows = rows.filter((t) => statusFilter.includes(getDisplayStatus(t)));
    return rows;
  }, [noRefTrips, excludePast, statusFilter, today]);

  const displayRows = useMemo(() => {
    let rows = matchedRows;
    if (excludePast) rows = rows.filter((r) => !r.trip.startDate || r.trip.startDate >= today);
    if (statusFilter.length < ALL_STATUSES.length)
      rows = rows.filter((r) => statusFilter.includes(getDisplayStatus(r.trip)));
    if (filterMode === 'nonmatching')      rows = rows.filter((r) => !r.isExact);
    else if (filterMode === 'discrepancy') rows = rows.filter((r) => r.vto && !r.isExact);
    else if (filterMode === 'nomatch')     rows = rows.filter((r) => !r.vto);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.trip.travellerName || '').toLowerCase().includes(q) ||
          (r.trip.vtoTripId     || '').toLowerCase().includes(q) ||
          (r.trip.tripRef       || '').toLowerCase().includes(q) ||
          (r.trip.clientId      || '').toLowerCase().includes(q) ||
          (r.vto?.client        || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [matchedRows, filterMode, statusFilter, search, excludePast, today]);

  const doExportCsv = () => {
    if (!displayRows.length) return;
    const headers = [
      'VTO Trip #', 'Portal Ref', 'Client', 'Traveller', 'Travel Date', 'Status',
      'Portal Gross (inc GST)', 'VTO Gross', 'Difference', 'Result', 'FX Converted Fare',
      'Received', 'Client Balance',
    ];
    const rows = displayRows.map((r) => {
      const ds = getDisplayStatus(r.trip);
      return [
        r.trip.vtoTripId     || '',
        r.trip.tripRef       || '',
        r.trip.clientId      || '',
        r.trip.travellerName || '',
        fmtDate(r.trip.startDate),
        STATUS_LABEL[ds] || ds,
        r.portalGross.toFixed(2),
        r.vtoGross != null     ? r.vtoGross.toFixed(2)        : '',
        r.diff     != null     ? r.diff.toFixed(2)            : '',
        !r.vto ? 'Not in VTO file' : r.isExact ? 'Match' : 'Discrepancy',
        r.fxConverted ? 'Yes' : '',
        r.vto ? r.vto.received.toFixed(2)      : '',
        r.vto ? r.vto.clientBalance.toFixed(2) : '',
      ];
    });
    exportCSV([headers, ...rows], `VTO_Reconciliation_${today}.csv`);
  };

  // ── Upload screen ─────────────────────────────────────────────────────────
  if (!vtoRows) {
    return (
      <div>
        <p className="text-gray-600 text-sm mb-6">
          Upload your Balance Summary Audit export to reconcile VTO trip amounts against the portal.
        </p>
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload Balance Summary Audit file"
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-teal-500 bg-teal-50'
              : 'border-gray-300 bg-white hover:border-teal-400 hover:bg-gray-50'
          }`}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); parseFile(e.dataTransfer.files[0]); }}
        >
          <Upload className="mx-auto mb-3 text-gray-400" size={36} aria-hidden="true" />
          <p className="font-semibold text-gray-700 text-base">Drop your Balance Summary Audit file here</p>
          <p className="text-gray-400 text-sm mt-1">or click to browse — accepts .xlsx or .xls</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => parseFile(e.target.files[0])}
        />
      </div>
    );
  }

  // ── Results screen ────────────────────────────────────────────────────────
  const FILTERS = [
    { id: 'nonmatching', label: 'Non-matching',    count: summary.nonMatching },
    { id: 'discrepancy', label: 'Amount mismatch', count: summary.discrepancies },
    { id: 'nomatch',     label: 'Not in VTO file', count: summary.noVtoMatch },
    { id: 'all',         label: 'All',             count: summary.matched },
  ];

  return (
    <div className="space-y-6">
      {/* File name + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-gray-500 text-sm">
          File: <span className="font-mono text-gray-700">{fileName}</span>
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => { setVtoRows(null); setFileName(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={13} aria-hidden="true" /> Upload new file
          </button>
          <button
            onClick={doExportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
          >
            <Download size={13} aria-hidden="true" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-gray-900">{summary.vtoRecords}</div>
          <div className="text-xs font-semibold text-gray-500 uppercase mt-0.5">VTO Records</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-emerald-700">{summary.exact}</div>
          <div className="text-xs font-semibold text-emerald-600 uppercase mt-0.5">Matched</div>
        </div>
        <div className={`border rounded-xl p-4 ${summary.discrepancies > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
          <div className={`text-2xl font-bold ${summary.discrepancies > 0 ? 'text-red-700' : 'text-gray-900'}`}>
            {summary.discrepancies}
          </div>
          <div className={`text-xs font-semibold uppercase mt-0.5 ${summary.discrepancies > 0 ? 'text-red-600' : 'text-gray-500'}`}>
            Amount Mismatch
          </div>
        </div>
        <div className={`border rounded-xl p-4 ${summary.noVtoMatch > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <div className={`text-2xl font-bold ${summary.noVtoMatch > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
            {summary.noVtoMatch}
          </div>
          <div className={`text-xs font-semibold uppercase mt-0.5 ${summary.noVtoMatch > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
            Not in VTO File
          </div>
        </div>
        <div className={`border rounded-xl p-4 ${summary.noRef > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'}`}>
          <div className={`text-2xl font-bold ${summary.noRef > 0 ? 'text-orange-700' : 'text-gray-900'}`}>
            {summary.noRef}
          </div>
          <div className={`text-xs font-semibold uppercase mt-0.5 ${summary.noRef > 0 ? 'text-orange-600' : 'text-gray-500'}`}>
            No VTO Ref Set
          </div>
        </div>
      </div>

      {/* Net difference bar — only shown when mismatches exist */}
      {summary.discrepancies > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-3 flex flex-wrap gap-6 text-sm">
          <div>
            <span className="text-gray-500">Portal total (mismatched trips):</span>{' '}
            <span className="font-bold text-gray-900">
              {fmt$(matchedRows.filter(r => r.vto && !r.isExact).reduce((s, r) => s + r.portalGross, 0))}
            </span>
          </div>
          <div>
            <span className="text-gray-500">VTO total (mismatched trips):</span>{' '}
            <span className="font-bold text-gray-900">
              {fmt$(matchedRows.filter(r => r.vto && !r.isExact).reduce((s, r) => s + (r.vtoGross || 0), 0))}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Net difference:</span>{' '}
            <span className="font-bold text-red-600">
              {fmtDiff(matchedRows.filter(r => r.vto && !r.isExact).reduce((s, r) => s + r.diff, 0))}
            </span>
          </div>
          {matchedRows.some(r => r.vto && !r.isExact && r.fxConverted) && (
            <p className="w-full text-xs text-gray-700 border-t border-gray-200 pt-2.5">
              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded text-[10px] font-semibold">FX</span>
              {' '}marks a trip that includes a fare the airline priced in a foreign currency. The
              portal figure is an exchange rate captured at the time of search, so a small
              difference against what settled may be the rate rather than an error.
            </p>
          )}
        </div>
      )}

      {/* Filter tabs + controls */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 flex-wrap" role="group" aria-label="Filter by reconciliation result">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterMode(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === f.id
                    ? 'bg-teal-700 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-400'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  filterMode === f.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>{f.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-600">
              <input
                type="checkbox"
                checked={excludePast}
                onChange={(e) => setExcludePast(e.target.checked)}
                className="w-4 h-4 rounded accent-teal-600 cursor-pointer"
              />
              Exclude past travel dates
            </label>
            <input
              type="text"
              placeholder="Search traveller, VTO #, portal ref…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search reconciliation results"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-gray-500 w-64"
            />
          </div>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
          <button
            onClick={() => setStatusFilter([...ALL_STATUSES])}
            className="text-xs text-teal-600 hover:underline"
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter([])}
            className="text-xs text-gray-400 hover:underline"
          >
            None
          </button>
          {ALL_STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? STATUS_PILL[s] + ' border-transparent'
                    : 'bg-white border-gray-200 text-gray-400'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main reconciliation table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">VTO Trip #</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Portal Ref</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Client</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Traveller</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Travel Date</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Status</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-right">Portal Gross (inc GST)</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-right">VTO Gross</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-right">Difference</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-center">Result</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-right">Received</th>
                <th scope="col" className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase text-right">Client Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-gray-400 text-sm">
                    {filterMode === 'nonmatching'
                      ? 'All portal trips match VTO — nothing to reconcile.'
                      : 'No trips match the current filter.'}
                  </td>
                </tr>
              )}
              {displayRows.map((r) => {
                const ds     = getDisplayStatus(r.trip);
                const rowBg  = !r.vto ? 'bg-amber-50' : 'bg-red-50';
                return (
                  <tr
                    key={r.trip.id}
                    onClick={() => navigate(`/travel?trip=${r.trip.id}`)}
                    className={`${rowBg} cursor-pointer hover:brightness-95 transition-all`}
                    title="Open trip"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">
                      {r.trip.vtoTripId || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {r.trip.tripRef || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {r.trip.clientId || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {r.trip.travellerName || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {fmtDate(r.trip.startDate)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[ds] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABEL[ds] || ds}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-800">
                      {fmt$(r.portalGross)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-800">
                      {r.vto
                        ? fmt$(r.vtoGross)
                        : <span className="text-gray-400 italic text-xs">not in file</span>}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${
                      r.diff == null ? 'text-gray-400' :
                      r.diff > 0     ? 'text-red-700'  : 'text-orange-700'
                    }`}>
                      {fmtDiff(r.diff)}
                      {/* Why a few dollars might be nothing to chase. Marked rather than
                          auto-excused — how big a gap an exchange rate can explain is a judgement
                          for whoever is reconciling, not something to decide for them. */}
                      {r.fxConverted && (
                        <span
                          className="ml-1.5 px-1.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded text-[10px] font-semibold align-middle"
                          title="Includes a fare the airline priced in a foreign currency">
                          FX
                          <span className="sr-only">
                            {' '}— includes a fare the airline priced in a foreign currency, so the
                            portal amount is an exchange rate captured at the time of search
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {!r.vto
                        ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Not in file</span>
                        : <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">⚠ Mismatch</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {r.vto ? fmt$(r.vto.received) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${
                      r.vto
                        ? (r.vto.clientBalance > 0 ? 'text-amber-700' : 'text-gray-700')
                        : 'text-gray-400'
                    }`}>
                      {r.vto ? fmt$(r.vto.clientBalance) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {displayRows.length > 0 && (
          <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
            Showing {displayRows.length} trip{displayRows.length !== 1 ? 's' : ''} requiring attention
          </div>
        )}
      </div>

      {/* Portal trips with no VTO reference number */}
      {displayNoRef.length > 0 && (
        <div className="bg-white border border-orange-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-orange-50 border-b border-orange-200 flex items-center gap-2">
            <span className="text-orange-700 font-bold text-sm">Portal Trips Missing VTO Reference</span>
            <span className="ml-auto text-xs text-orange-500 font-medium">
              {displayNoRef.length} trip{displayNoRef.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Portal Ref</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Client</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Traveller</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Travel Date</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Destination</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Status</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase text-right">Portal Gross (inc GST)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayNoRef.map((t) => {
                  const ds = getDisplayStatus(t);
                  return (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/travel?trip=${t.id}`)}
                      className="hover:bg-orange-100 transition-colors cursor-pointer"
                      title="Open trip"
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">
                          {t.tripRef || t.id}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">{t.clientId || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-800">{t.travellerName || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(t.startDate)}</td>
                      <td className="px-4 py-2.5 text-gray-600">{t.destinationCity || '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[ds] || 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_LABEL[ds] || ds}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-800">
                        {fmt$(tripGrossInclGST(t))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
            These STX-Managed trips have no VTO reference number set. Open the trip to add the VTO # if applicable.
          </div>
        </div>
      )}
    </div>
  );
}
